import * as z from 'zod'
import { zodQs, jsonSchemaField } from '../utils'
import { LanguageModel } from './constants'
import { MediaSchema, MIME_TYPE_REGEX } from './Media'

export enum AgentStatus {
    Draft = 'draft',
    Active = 'active',
    Inactive = 'inactive',
}

export enum AgentVisibility {
    Public = 'public',
    Private = 'private',
    Organization = 'organization',
}

export enum AgentType {
    Chat = 'chat',
    Reasoning = 'reasoning',
}

export enum AgentSearchMode {
    Semantic = 'semantic',
    Balanced = 'balanced',
    Keyword = 'keyword',
}

/**
 * Provider-specific reasoning configurations.
 *
 * Each provider has its own reasoning API:
 * - OpenAI (GPT-5, o-series): `reasoningEffort` + `reasoningSummary`
 * - Anthropic (Claude 4.6+): adaptive thinking with `effort` levels, or legacy `budgetTokens`
 * - Google (Gemini 3/3.1): `thinkingLevel`; Gemini 2.5: `thinkingBudget`
 * - DeepSeek: on/off thinking toggle
 *
 * Only the matching provider's config is used at runtime (based on model prefix).
 * Admins can pre-configure multiple providers for easy model switching.
 */

export const OpenAIReasoningSchema = z.object({
    reasoningEffort: z
        .enum(['none', 'low', 'medium', 'high', 'xhigh'])
        .optional(),
    reasoningSummary: z.enum(['auto', 'detailed', 'concise']).optional(),
})
export type OpenAIReasoning = z.infer<typeof OpenAIReasoningSchema>

export const AnthropicReasoningSchema = z.object({
    type: z.enum(['adaptive', 'enabled', 'disabled']).optional(),
    effort: z.enum(['low', 'medium', 'high', 'xhigh', 'max']).optional(),
    display: z.enum(['summarized', 'omitted']).optional(),
    budgetTokens: z.number().int().positive().optional(),
})
export type AnthropicReasoning = z.infer<typeof AnthropicReasoningSchema>

export const GoogleReasoningSchema = z.object({
    thinkingLevel: z.enum(['minimal', 'low', 'medium', 'high']).optional(),
    thinkingBudget: z.number().int().optional(),
    includeThoughts: z.boolean().optional(),
})
export type GoogleReasoning = z.infer<typeof GoogleReasoningSchema>

export const DeepSeekReasoningSchema = z.object({
    type: z.enum(['enabled', 'disabled']).optional(),
})
export type DeepSeekReasoning = z.infer<typeof DeepSeekReasoningSchema>

/**
 * Migrates legacy format `{ effort: 'high', reasoningSummary: 'auto' }` to the new
 * per-provider format. Applied via preprocess so old DB data auto-migrates on parse.
 */
function migrateReasoningConfig(val: unknown): unknown {
    if (!val || typeof val !== 'object') {
        return val
    }
    const obj = val as Record<string, unknown>
    // Detect legacy format: has `effort` key with old enum values
    if (
        'effort' in obj &&
        !(
            'openai' in obj ||
            'anthropic' in obj ||
            'google' in obj ||
            'deepseek' in obj
        )
    ) {
        const effort = obj.effort as string | undefined
        const reasoningSummary = obj.reasoningSummary as string | undefined
        const migrated: Record<string, unknown> = {}

        if (effort && effort !== 'off') {
            migrated.openai = {
                reasoningEffort: effort,
                ...(reasoningSummary ? { reasoningSummary } : {}),
            }
            migrated.anthropic = {
                type: 'adaptive' as const,
                effort: effort as 'low' | 'medium' | 'high',
            }
            migrated.google = {
                thinkingLevel: effort as 'low' | 'medium' | 'high',
            }
            migrated.deepseek = { type: 'enabled' as const }
        } else {
            migrated.openai = { reasoningEffort: 'none' }
            migrated.deepseek = { type: 'disabled' as const }
        }
        return migrated
    }
    return val
}

export const AgentReasoningSchema = z.preprocess(
    migrateReasoningConfig,
    z
        .object({
            openai: OpenAIReasoningSchema.optional(),
            anthropic: AnthropicReasoningSchema.optional(),
            google: GoogleReasoningSchema.optional(),
            deepseek: DeepSeekReasoningSchema.optional(),
        })
        .optional(),
)

export type AgentReasoning = z.infer<typeof AgentReasoningSchema>

/**
 * Whether reasoning is effectively enabled for the given model.
 *
 * Mirrors the runtime semantics of `getReasoningProviderOptions` (backend):
 * only the provider config matching the model prefix is considered.
 * Note: for Anthropic with `display: 'omitted'` reasoning runs but no
 * reasoning parts are streamed to the client.
 */
export function isReasoningEnabled(
    model: string,
    reasoning: AgentReasoning | null | undefined,
): boolean {
    if (!reasoning) {
        return false
    }

    const [provider = ''] = model.split('/')

    if (provider === 'openai') {
        const config = reasoning.openai
        return Boolean(
            config?.reasoningEffort && config.reasoningEffort !== 'none',
        )
    }

    if (provider === 'anthropic') {
        const config = reasoning.anthropic
        return Boolean(config && config.type !== 'disabled')
    }

    if (provider === 'google') {
        const config = reasoning.google
        if (!config || config.thinkingBudget === 0) {
            return false
        }
        return Boolean(
            (config.thinkingLevel && config.thinkingLevel !== 'minimal') ||
            config.thinkingBudget ||
            config.includeThoughts,
        )
    }

    if (provider === 'deepseek') {
        return reasoning.deepseek?.type === 'enabled'
    }

    return false
}

export enum AgentBuiltInTool {
    Geocode = 'geocode',
    GetWeather = 'getWeather',
    ReadDocumentFile = 'readDocumentFile',
    RequestConfirm = 'requestConfirm',
    RequestGeolocation = 'requestGeolocation',
    RetrieveRevisedAnswers = 'retrieveRevisedAnswers',
    SearchInKnowledgeBase = 'searchInKnowledgeBase',
    SearchInUserFiles = 'searchInUserFiles',
    UserMemory = 'userMemory',
    ShowArtifact = 'showArtifact',
    ShowCalendarEvent = 'showCalendarEvent',
    ShowComparison = 'showComparison',
    ShowContactForm = 'showContactForm',
    ShowDiagram = 'showDiagram',
    ShowEmail = 'showEmail',
    ShowImageGallery = 'showImageGallery',
    ShowLocation = 'showLocation',
    ShowMessage = 'showMessage',
    ShowForm = 'showForm',
    ShowProductList = 'showProductList',
    ShowQrCode = 'showQrCode',
    ShowSources = 'showSources',
    ShowSuggestedReply = 'showSuggestedReply',
    ShowWeather = 'showWeather',
    ShowWebPages = 'showWebPages',
    // deprecated tools - to be removed in favor of more specific tools above
    ShowMultipleChoice = 'showMultipleChoice',
}

/**
 * Returns true if the tool renders a UI element (card, form, list, etc.).
 * Used in `prepareStep` to prevent duplicate UI elements across streaming steps.
 */
export function isPresentationTool(toolName: string): boolean {
    return (
        toolName === AgentBuiltInTool.RequestConfirm ||
        toolName === AgentBuiltInTool.ShowArtifact ||
        toolName === AgentBuiltInTool.ShowCalendarEvent ||
        toolName === AgentBuiltInTool.ShowComparison ||
        toolName === AgentBuiltInTool.ShowContactForm ||
        toolName === AgentBuiltInTool.ShowDiagram ||
        toolName === AgentBuiltInTool.ShowEmail ||
        toolName === AgentBuiltInTool.ShowImageGallery ||
        toolName === AgentBuiltInTool.ShowLocation ||
        toolName === AgentBuiltInTool.ShowMessage ||
        toolName === AgentBuiltInTool.ShowForm ||
        toolName === AgentBuiltInTool.ShowMultipleChoice ||
        toolName === AgentBuiltInTool.ShowProductList ||
        toolName === AgentBuiltInTool.ShowQrCode ||
        toolName === AgentBuiltInTool.ShowSources ||
        toolName === AgentBuiltInTool.ShowSuggestedReply ||
        toolName === AgentBuiltInTool.ShowWeather ||
        toolName === AgentBuiltInTool.ShowWebPages
    )
}

/**
 * Tools that render natively as cards on messaging platforms (Teams/WhatsApp).
 * Must mirror the coverage of the backend `platformToolRenderer` (the renderers
 * + the resumable confirm/form and the tappable suggested-reply). Tools not in
 * this set fall back to the model's plain-text reply on those channels.
 */
export const PLATFORM_RENDERED_TOOLS = new Set<string>([
    AgentBuiltInTool.RequestConfirm,
    AgentBuiltInTool.ShowArtifact,
    AgentBuiltInTool.ShowWeather,
    AgentBuiltInTool.ShowLocation,
    AgentBuiltInTool.ShowComparison,
    AgentBuiltInTool.ShowProductList,
    AgentBuiltInTool.ShowWebPages,
    AgentBuiltInTool.ShowImageGallery,
    AgentBuiltInTool.ShowSources,
    AgentBuiltInTool.ShowSuggestedReply,
    AgentBuiltInTool.ShowCalendarEvent,
    AgentBuiltInTool.ShowEmail,
    AgentBuiltInTool.ShowMessage,
    AgentBuiltInTool.ShowForm,
])

/** True if the tool renders natively on Teams/WhatsApp (not just web). */
export function isPlatformRenderedTool(toolName: string): boolean {
    return PLATFORM_RENDERED_TOOLS.has(toolName)
}

export const AgentFormSettingsSchema = z.object({
    maxSteps: z.number().int().min(1).max(10).optional().default(5),
    maxOptionsPerStep: z.number().int().min(2).max(10).optional().default(5),
})
export type AgentFormSettings = z.infer<typeof AgentFormSettingsSchema>

export const DEFAULT_MAX_STEP_COUNT = 10
export const DEFAULT_SUB_AGENT_MAX_STEP_COUNT = 6
export const DEFAULT_ENDPOINT_MAX_STEP_COUNT = 5

export enum WebSearchProvider {
    Perplexity = 'perplexity',
    Parallel = 'parallel',
    Native = 'native',
}

export const PerplexitySearchConfigSchema = z.object({
    maxResults: z.number().int().min(1).max(20).optional(),
    maxTokens: z.number().int().positive().max(1_000_000).optional(),
    maxTokensPerPage: z.number().int().positive().optional(),
    country: z.string().max(2).optional(),
    searchLanguageFilter: z.array(z.string().max(5)).max(10).optional(),
    searchDomainFilter: z.array(z.string()).max(20).optional(),
    searchRecencyFilter: z.enum(['day', 'week', 'month', 'year']).optional(),
})
export type PerplexitySearchConfig = z.infer<
    typeof PerplexitySearchConfigSchema
>

export const ParallelSearchConfigSchema = z.object({
    mode: z.enum(['one-shot', 'agentic']).optional(),
    maxResults: z.number().int().min(1).max(20).optional(),
    sourcePolicy: z
        .object({
            includeDomains: z.array(z.string()).optional(),
            excludeDomains: z.array(z.string()).optional(),
            afterDate: z.string().optional(),
        })
        .optional(),
    excerpts: z
        .object({
            maxCharsPerResult: z.number().int().positive().optional(),
            maxCharsTotal: z.number().int().positive().optional(),
        })
        .optional(),
    fetchPolicy: z
        .object({
            maxAgeSeconds: z.number().int().positive().optional(),
        })
        .optional(),
})
export type ParallelSearchConfig = z.infer<typeof ParallelSearchConfigSchema>

export const NativeWebSearchConfigSchema = z.object({
    openai: z.object({}).optional(),
    anthropic: z
        .object({
            maxUses: z.number().int().positive().optional(),
            allowedDomains: z.array(z.string()).optional(),
            blockedDomains: z.array(z.string()).optional(),
            userLocation: z
                .object({
                    type: z.literal('approximate').optional(),
                    country: z.string().optional(),
                    region: z.string().optional(),
                    city: z.string().optional(),
                    timezone: z.string().optional(),
                })
                .optional(),
        })
        .optional(),
    google: z.object({}).optional(),
})
export type NativeWebSearchConfig = z.infer<typeof NativeWebSearchConfigSchema>

export const AgentWebSearchSchema = z.object({
    enabled: z.boolean().default(false),
    provider: z
        .enum(WebSearchProvider)
        .optional()
        .default(WebSearchProvider.Native),
    perplexity: PerplexitySearchConfigSchema.optional(),
    parallel: ParallelSearchConfigSchema.optional(),
    native: NativeWebSearchConfigSchema.optional(),
})
export type AgentWebSearch = z.infer<typeof AgentWebSearchSchema>

// Cross-agent personalization sub-options. Default OFF = current agent-bound
// behavior. Only meaningful when the parent tool (UserMemory / SearchInUserFiles)
// is enabled in `disabled`.
export const AgentUserMemorySchema = z.object({
    // Read the user's memories across ALL their agents (aggregated), not just
    // this agent's. Writes stay per-agent (scope='agent').
    useAllUserKb: z.boolean().default(false),
})
export type AgentUserMemory = z.infer<typeof AgentUserMemorySchema>

export const AgentUserFilesSchema = z.object({
    // Search ALL the user's files, not just those pinned to this agent
    // (or globally shared agent_id IS NULL).
    useAllUserFiles: z.boolean().default(false),
})
export type AgentUserFiles = z.infer<typeof AgentUserFilesSchema>

export const AgentToolsSchema = z.object({
    // New agents start with user memory and user-files search OFF (opt-in
    // personalization). Existing agents keep whatever `disabled` they stored.
    disabled: z
        .array(z.enum(AgentBuiltInTool))
        .default([
            AgentBuiltInTool.UserMemory,
            AgentBuiltInTool.SearchInUserFiles,
        ]),
    showForm: AgentFormSettingsSchema.optional().default(() =>
        AgentFormSettingsSchema.parse({}),
    ),
    webSearch: AgentWebSearchSchema.optional().default(() =>
        AgentWebSearchSchema.parse({}),
    ),
    userMemory: AgentUserMemorySchema.optional().default(() =>
        AgentUserMemorySchema.parse({}),
    ),
    userFiles: AgentUserFilesSchema.optional().default(() =>
        AgentUserFilesSchema.parse({}),
    ),
})
export type AgentTools = z.infer<typeof AgentToolsSchema>

/**
 * Ranking configuration schema for smart chunk retrieval.
 * Controls freshness boost, document priority, and MMR.
 * Semantic deduplication is always applied at sync time (not configurable per-agent).
 */
export const AgentRankingSchema = z
    .object({
        freshness: z
            .object({
                enabled: z.boolean().default(false),
                /** Half-life in seconds (1 day – 1 year) */
                scale: z
                    .number()
                    .int()
                    .min(86400)
                    .max(31536000)
                    .default(7776000),
                /** Weight multiplier for freshness boost in formula */
                weight: z.number().min(0.05).max(0.5).default(0.1),
            })
            .prefault({}),
        priority: z
            .object({
                /** Always enabled by default — priority is per-document, not per-agent */
                enabled: z.boolean().default(true),
            })
            .prefault({}),
        mmr: z
            .object({
                enabled: z.boolean().default(false),
                /** Diversity tradeoff (0.0 = relevance only, 1.0 = diversity only) */
                diversity: z.number().min(0.0).max(1.0).default(0.3),
                /** Candidates pre-selected before MMR (semantic mode only) */
                candidatesLimit: z.number().int().min(50).max(200).default(100),
            })
            .prefault({}),
    })
    .prefault({})
export type AgentRanking = z.infer<typeof AgentRankingSchema>

export const AgentModelSchema = z.object({
    prompt: z.string().optional(),
    temperature: z.number().min(0).max(2).optional().default(0.6),
    maxOutputTokens: z.number().positive().optional(),
    presencePenalty: z.number().min(-2).max(2).optional(),
    frequencyPenalty: z.number().min(-2).max(2).optional(),
    searchMode: z
        .enum(AgentSearchMode)
        .optional()
        .default(AgentSearchMode.Balanced),
    limit: z.number().positive().max(100).optional().default(10),
    maxStepCount: z
        .number()
        .int()
        .min(1)
        .max(40)
        .optional()
        .default(DEFAULT_MAX_STEP_COUNT),
    reasoning: AgentReasoningSchema,
    /** Smart ranking configuration (freshness, priority, MMR) */
    ranking: AgentRankingSchema.optional().default(() =>
        AgentRankingSchema.parse({}),
    ),
})
export type AgentModel = z.infer<typeof AgentModelSchema>

export const LocalizedStringSchema = z.preprocess(
    (val) => (typeof val === 'string' ? { it: val } : val),
    z.record(z.string(), z.string()),
)
export type LocalizedString = z.infer<typeof LocalizedStringSchema>

export const DEFAULT_WIDGET_URL =
    'https://cdn.jsdelivr.net/npm/@8wave/ai-elements/dist/ai-elements.es.js'

export enum AgentLoaderStrategy {
    InlineModule = 'inline-module',
    ScriptSrc = 'script-src',
    DynamicImport = 'dynamic-import',
}

export enum AgentChannel {
    Frontoffice = 'frontoffice',
    App = 'app',
    Widget = 'widget',
}

export const ALL_AGENT_CHANNELS = Object.values(AgentChannel) as AgentChannel[]

export const AgentInterfaceSchema = z.object({
    media: MediaSchema.optional(),
    logo: z.string().optional(),
    theme: z.enum(['light', 'dark', 'auto']).optional().default('auto'),
    mainColor: z
        .string()
        .regex(/^#[0-9A-Fa-f]{6}$/)
        .optional()
        .default('#000000'),
    textColor: z.enum(['auto', 'white', 'black']).optional().default('auto'),
    useColorsForAgentHeader: z.boolean().optional().default(false),
    initialMessage: LocalizedStringSchema.optional(),
    greeting: z.array(LocalizedStringSchema).min(1).max(10).optional(),
    messagePlaceholder: LocalizedStringSchema.optional(),
    dismissableNotice: LocalizedStringSchema.optional(),
    footerMessage: LocalizedStringSchema.optional(),
    privacyPolicyNotice: LocalizedStringSchema.optional(),
    welcomePopup: z.boolean().optional().default(false),
    floatingButtonPosition: z
        .enum(['left', 'right'])
        .optional()
        .default('right'),
    maxMessageLength: z.number().int().positive().optional(),
    widgetUrl: z.url().default(DEFAULT_WIDGET_URL),
    loadDelay: z.number().int().min(0).max(30000).optional().default(0),
    loaderStrategy: z
        .enum(AgentLoaderStrategy)
        .optional()
        .default(AgentLoaderStrategy.InlineModule),
    customCss: z.string().optional(),
    smoothStream: z.boolean().optional().default(false),
    /**
     * Show every agent step (reasoning, tool calls) as an expanded timeline
     * in the chat instead of the compact activity indicator. Only meaningful
     * when reasoning is enabled on the agent model.
     */
    showExtendedSteps: z.boolean().optional().default(false),
})
export type AgentInterface = z.infer<typeof AgentInterfaceSchema>

export const CHAT_FILE_UPLOAD_DEFAULT_MIME_TYPES = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'application/pdf',
    'text/plain',
    'text/markdown',
    'text/csv',
] as const

export type ChatFileUploadMimeType =
    (typeof CHAT_FILE_UPLOAD_DEFAULT_MIME_TYPES)[number]

export const CHAT_FILE_UPLOAD_MAX_FILE_SIZE_BYTES = 104_857_600 // 100 MB

export const AgentFileUploadSchema = z.object({
    enabled: z.boolean().default(false),
    maxFileSizeBytes: z
        .number()
        .int()
        .positive()
        .max(CHAT_FILE_UPLOAD_MAX_FILE_SIZE_BYTES)
        .optional()
        .default(20_971_520),
    allowedMimeTypes: z
        .array(z.string().regex(MIME_TYPE_REGEX))
        .optional()
        .default([...CHAT_FILE_UPLOAD_DEFAULT_MIME_TYPES]),
    maxFilesPerMessage: z
        .number()
        .int()
        .positive()
        .max(10)
        .optional()
        .default(5),
    maxFilesPerChat: z.number().int().positive().max(50).optional().default(20),
})
export type AgentFileUpload = z.infer<typeof AgentFileUploadSchema>

export const AgentGatewayOptionsSchema = z.object({
    zeroDataRetention: z.boolean().default(true),
    disallowPromptTraining: z.boolean().default(true),
    providerTimeoutMs: z.number().int().min(1000).max(789000).optional(),
})
export type AgentGatewayOptions = z.infer<typeof AgentGatewayOptionsSchema>

export const AgentSettingsSchema = z.object({
    model: AgentModelSchema,
    interface: AgentInterfaceSchema,
    tools: AgentToolsSchema.optional().default(() =>
        AgentToolsSchema.parse({}),
    ),
    fileUpload: AgentFileUploadSchema.optional().default(() =>
        AgentFileUploadSchema.parse({}),
    ),
    gatewayOptions: AgentGatewayOptionsSchema.optional().default(() =>
        AgentGatewayOptionsSchema.parse({}),
    ),
    channels: z
        .array(z.enum(AgentChannel))
        .optional()
        .default(() => [...ALL_AGENT_CHANNELS]),
    /**
     * JSON Schema definition for the `externalContext` field accepted in chat
     * requests. Only top-level string-keyed properties that match this schema
     * will be forwarded to the LLM. When absent, all external context is
     * silently ignored (deny-by-default).
     */
    externalContextSchema: jsonSchemaField.optional(),
})
export type AgentSettings = z.infer<typeof AgentSettingsSchema>

export const AgentSecuritySchema = z.object({
    rateLimits: z.object({
        minuteRateLimit: z.number().int().min(0).optional(),
        hourRateLimit: z.number().int().min(0).optional(),
        dayRateLimit: z.number().int().min(0).optional(),
        monthRateLimit: z.number().int().min(0).optional(),
    }),
    trustedOrigins: z
        .object({
            origins: z
                .array(z.url().or(z.literal('')))
                .transform((items) => {
                    return items?.filter((item) => item?.trim())
                })
                .default([]),
        })
        .default({ origins: [] }),
})
export type AgentSecurity = z.infer<typeof AgentSecuritySchema>

export const AgentDtoSchema = z.object({
    id: z.string().optional(),
    name: z.string(),
    prompt: z.string().optional(),
    model: z.string().default(LanguageModel.gpt52Chat),
    fallbackModels: z.array(z.string()).default([]),
    status: z.enum(AgentStatus).default(AgentStatus.Draft),
    visibility: z.enum(AgentVisibility).default(AgentVisibility.Organization),
    settings: AgentSettingsSchema,
    security: AgentSecuritySchema.nullish(),
    type: z.enum(AgentType).default(AgentType.Chat),
    organizationId: z.string(),
})

export const AgentSchema = z.lazy(() =>
    z.object({
        id: z.string(),
        name: z.string(),
        prompt: z.string(),
        model: z.string(),
        fallbackModels: z.array(z.string()).nullable().default([]),
        status: z.enum(AgentStatus).default(AgentStatus.Draft),
        visibility: z
            .enum(AgentVisibility)
            .default(AgentVisibility.Organization),
        settings: AgentSettingsSchema,
        security: AgentSecuritySchema.nullable(),
        type: z.enum(AgentType).default(AgentType.Chat),
        organizationId: z.string(),
        createdAt: z.coerce.date(),
        createdBy: z.string(),
        updatedAt: z.coerce.date(),
        updatedBy: z.string(),
        deleted: z.boolean(),
        deletedAt: z.coerce.date().nullable(),
        deletedBy: z.string().nullable(),
        outOfBudget: z.boolean().optional().default(false),
        outOfBudgetReason: z.string().optional().default(''),
    }),
)

/* -------------------------------------------------------------------------- *
 *  Agent exposure tiers
 *
 *  The same agent is serialized at decreasing levels of detail depending on
 *  who is asking. Each tier is a strict narrowing of the one above it:
 *
 *    Agent          Full internal entity — prompt, model, settings, security.
 *                   Never leaves the trusted (admin/backoffice) boundary.
 *    AgentPublic    Client view for a viewer who ALREADY HAS ACCESS. "Public"
 *                   means "safe to expose to the client", NOT "visibility =
 *                   public": a private/organization agent is still served as
 *                   AgentPublic once the viewer is authorized.
 *    AgentBranding  Minimal, safe subset shown to ANONYMOUS viewers on the
 *                   pre-login screen — just enough to render branded chrome.
 *    AgentAccess    Envelope returned by the `/access` endpoint: a discriminated
 *                   union on `requiresAuth` that resolves to either an
 *                   AgentPublic (access granted) or an AgentBranding (login
 *                   required), plus the available auth methods.
 * -------------------------------------------------------------------------- */

export const AgentPublicSchema = z.object({
    id: z.string(),
    name: z.string(),
    status: z.enum(AgentStatus),
    visibility: z.enum(AgentVisibility),
    organizationId: z.string(),
    interface: AgentInterfaceSchema,
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    capabilities: z.object({
        fileUpload: AgentFileUploadSchema,
    }),
    channels: z
        .array(z.enum(AgentChannel))
        .default(() => [...ALL_AGENT_CHANNELS]),
})

/**
 * Safe subset of `AgentInterface` exposed to anonymous users on the public
 * "access" endpoint to render a branded login screen. Mirrors the field names
 * of `AgentInterfaceSchema` (via `pick`) so consumers can read branding the
 * same way for both `AgentPublic.interface` and `AgentBranding.interface`.
 *
 * 🔒 NEVER add sensitive fields here (system prompt, models, tools, …).
 */
export const AgentBrandingInterfaceSchema = AgentInterfaceSchema.pick({
    logo: true,
    mainColor: true,
    textColor: true,
    theme: true,
    floatingButtonPosition: true,
    customCss: true,
})
export type AgentBrandingInterface = z.infer<
    typeof AgentBrandingInterfaceSchema
>

export const AgentBrandingSchema = AgentPublicSchema.pick({
    id: true,
    name: true,
    organizationId: true,
}).extend({
    interface: AgentBrandingInterfaceSchema,
})
export type AgentBranding = z.infer<typeof AgentBrandingSchema>

export const AuthMethodsSchema = z.object({
    emailPassword: z.boolean(),
    google: z.boolean(),
    microsoft: z.boolean(),
})
export type AuthMethods = z.infer<typeof AuthMethodsSchema>

/**
 * Access decision for an agent, WITHOUT the auth methods.
 *
 * Discriminated on `requiresAuth` so the two outcomes are mutually exclusive at
 * the type level — no caller has to null-check `agent`/`branding` by hand:
 *  - `requiresAuth: false` → access granted, full `agent` is present.
 *  - `requiresAuth: true`  → login required, only `branding` is present.
 *
 * This is what the service returns; the controller layers `authMethods` on top
 * to produce {@link AgentAccessSchema}.
 */
export const AgentAccessGrantedSchema = z.object({
    requiresAuth: z.literal(false),
    agent: AgentPublicSchema,
    branding: z.null(),
})

export const AgentAccessRequiresAuthSchema = z.object({
    requiresAuth: z.literal(true),
    agent: z.null(),
    branding: AgentBrandingSchema,
})

export const AgentAccessDecisionSchema = z.discriminatedUnion('requiresAuth', [
    AgentAccessGrantedSchema,
    AgentAccessRequiresAuthSchema,
])
export type AgentAccessDecision = z.infer<typeof AgentAccessDecisionSchema>

/**
 * Full payload returned by the public `/access` endpoint: the
 * {@link AgentAccessDecisionSchema} plus the auth methods the client may offer
 * on the login screen.
 */
export const AgentAccessSchema = z.discriminatedUnion('requiresAuth', [
    AgentAccessGrantedSchema.extend({ authMethods: AuthMethodsSchema }),
    AgentAccessRequiresAuthSchema.extend({ authMethods: AuthMethodsSchema }),
])
export type AgentAccess = z.infer<typeof AgentAccessSchema>

/**
 * Per-member allowlist of `organization`-visibility agents. An empty `agentIds`
 * means "no restriction" — the member can use every organization agent (the
 * default). A non-empty list limits the member to those agents. Owners/admins
 * always bypass it. `id` is the member's user id, so the resource can be
 * cached/updated as a keyed entity by the frontend store. Used as both the GET
 * response and the PUT body of the backoffice `/members/:userId/agent-access`
 * endpoint.
 */
export const MemberAgentAccessSchema = z.object({
    id: z.uuid(),
    agentIds: z.array(z.uuid()),
})
export type MemberAgentAccess = z.infer<typeof MemberAgentAccessSchema>

export const AgentsQuerystringSchema = z.object({
    ...zodQs.pagination(),
    ...zodQs.sort([
        'id',
        'updatedAt',
        'createdAt',
        'name',
        'type',
        'status',
        'visibility',
        'model',
    ]),
    ...zodQs.filters(['done', 'type']),
    ...zodQs.filter('id'),
    ...zodQs.fullText(),
    ...zodQs.ids(),
})

export type AgentDto = z.infer<typeof AgentDtoSchema>
export type Agent = z.infer<typeof AgentSchema>
export type AgentPublic = z.infer<typeof AgentPublicSchema>

export type AgentQuerystring = z.infer<typeof AgentsQuerystringSchema>
